/** Screen pixels, relative to the editor's viewport. */
export type ScreenRect = { x: number; y: number; w: number; h: number };
export type FreeArea = { top: number; right: number; bottom: number; left: number };
export type BarPlacement = { x: number; y: number; side: 'above' | 'below' | 'pinned' };

/** Between the selection and its context bar. */
export const CONTEXT_BAR_GAP = 8;
/** Kept free above a selected section: its title pill and grip sit there (P5e). */
export const SECTION_RESERVE_PX = 36;
/** Kept free above a single object that rotates: its rotate handle sits 24 px above it (`ROTATE_OFFSET_PX`). */
export const ROTATE_RESERVE_PX = 34;

/**
 * Where the selection's context bar goes (P5f, backlog §14.1): centred above the selection (below while it is being
 * edited: `prefer`), on the other side when the free area has no room, pinned to the top of the free area when neither
 * fits (a selection taller than the view); never outside the free area sideways. Null when the selection lies entirely
 * outside the free area.
 */
export function placeContextBar(
  sel: ScreenRect,
  size: { w: number; h: number },
  area: FreeArea,
  reserveAbove = 0,
  prefer: 'above' | 'below' = 'above',
): BarPlacement | null {
  if (sel.x + sel.w < area.left || sel.x > area.right || sel.y + sel.h < area.top || sel.y > area.bottom) return null;
  const x = Math.max(area.left, Math.min(sel.x + sel.w / 2 - size.w / 2, area.right - size.w));
  const above = sel.y - reserveAbove - CONTEXT_BAR_GAP - size.h;
  const below = sel.y + sel.h + CONTEXT_BAR_GAP;
  const fitsAbove = above >= area.top;
  const fitsBelow = below + size.h <= area.bottom;
  if (prefer === 'below' && fitsBelow) return { x, y: below, side: 'below' };
  if (fitsAbove) return { x, y: above, side: 'above' };
  if (fitsBelow) return { x, y: below, side: 'below' };
  return { x, y: area.top, side: 'pinned' };
}
